/**
 * The foot of the details screen: the reasons the user can't continue, and the
 * buttons that let them.
 *
 * Two blockers get their own message because the generic "correct the above
 * errors" banner points at nothing the user can see — a pickup date that falls
 * on a regional holiday, and one that has slipped into the past. Both offer to
 * move the date rather than just refusing.
 */

import SlideDown from "@/Shared/Animations/CustomComponents/SlideDown";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react/ArrowsClockwise";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";
import { BookShipmentDetailsErrorWarning } from "../CustomComponents/BookShipmentDetailsErrorWarning";
import { BookShipmentWarningMessages } from "../CustomComponents/BookShipmentWarningMessages";

// Resetting throws away everything typed so far, so both entry points ask first.
const RESET_CONFIRM = {
  title: "Reset Details",
  message: "Are you sure you want to reset?",
  confirmLabel: "Reset Details",
};

interface BookShipmentDetailsSubmitRowProps {
  isPickupDateHoliday: boolean;
  isPickupDateWeekend: boolean;
  pickupDateIsInThePast: boolean;
  forceValidation: boolean;
  showErrorsAtBottom: boolean;
  quoteRequestError: boolean;
  submittingQuotes: boolean;
  quotePreparationMessage: string;
  warnings: string[][];
  onScrollToPickupDate: () => void;
  onReset: () => void;
  onGetQuotesOrCreateAccount: () => void;
}

export function BookShipmentDetailsSubmitRow(
  props: BookShipmentDetailsSubmitRowProps,
) {
  return (
    <>
      {props.isPickupDateHoliday && (
        <BookShipmentDetailsErrorWarning>
          <div>
            The selected date is a holiday in the region of the pickup location.
            <br />
            Please{" "}
            <Link onClick={props.onScrollToPickupDate}>change the date</Link> to
            get instant quotes.
          </div>
        </BookShipmentDetailsErrorWarning>
      )}
      {/* Like the holiday case above, a past pickup date blocks booking, so it
        must be shown here — otherwise the user only sees the generic
        "correct the above errors" banner. Only shown once the user tries to
        continue; the calendar itself already flags past dates on hover. */}
      {props.forceValidation && props.pickupDateIsInThePast && (
        <BookShipmentDetailsErrorWarning>
          <div>
            The selected pickup date is in the past.
            <br />
            Please{" "}
            <Link onClick={props.onScrollToPickupDate}>change the date</Link> to
            get instant quotes.
          </div>
        </BookShipmentDetailsErrorWarning>
      )}
      <SlideDown show={props.quoteRequestError}>
        <BookShipmentDetailsErrorWarning>
          Something went wrong. Please retry your quote!
        </BookShipmentDetailsErrorWarning>
      </SlideDown>
      {props.quoteRequestError ? (
        <Button
          primary
          size="large"
          onClick={props.onReset}
          confirm={RESET_CONFIRM}
          icon={ArrowsClockwiseIcon}
        >
          Start Again
        </Button>
      ) : (
        <Row gap={4}>
          <Button size="link" onClick={props.onReset} confirm={RESET_CONFIRM}>
            Start Again
          </Button>
          <Button
            primary
            size="large"
            onClick={props.onGetQuotesOrCreateAccount}
            loading={props.submittingQuotes}
            icon={ArrowRightIcon}
            disabled={props.isPickupDateHoliday || props.isPickupDateWeekend}
            disabledMessage="Choose a weekday that isn't a public holiday for pickup"
            data-testid="get-instant-quotes-button"
          >
            {props.submittingQuotes
              ? props.quotePreparationMessage
              : "Get instant quotes from carriers"}
          </Button>
        </Row>
      )}
      {!props.showErrorsAtBottom && props.warnings.length > 0 && (
        <BookShipmentWarningMessages warnings={props.warnings} />
      )}
      {props.showErrorsAtBottom && (
        <T color="red">Please correct the above errors</T>
      )}
    </>
  );
}
